import styled, { css, keyframes } from 'styled-components';
import { borderRadius, color, zIndex, PlainButton, media, Theme } from '@morressier/ts';

export const DraftStyleWrapper = styled.div<{ hasBanner: boolean }>`
  margin-bottom: 4rem;
  padding: ${p => (p.hasBanner ? '0 2rem' : '3rem 2rem')};

  ${media.medium`
      padding: ${p => (p.hasBanner ? '0 6rem' : '3rem 6rem')};
  `}

  /**
  * We inherit the height of the container by default
  */

  .DraftEditor-root,
  .DraftEditor-editorContainer,
  .public-DraftEditor-content {
    height: inherit;
    text-align: initial;
  }

  .DraftEditor-root {
    position: relative;
  }

  /**
 * Zero-opacity background used to allow focus in IE. Otherwise, clicks
 * fall through to the placeholder.
 */

  .DraftEditor-editorContainer {
    background-color: rgba(255, 255, 255, 0);
    /* Repair mysterious missing Safari cursor */
    border: 1px solid transparent;
    position: relative;
    z-index: 1;
  }

  .public-DraftEditor-content {
    outline: none;
    white-space: pre-wrap;
  }

  .public-DraftEditor-block {
    position: relative;
  }

  .DraftEditor-alignLeft .public-DraftEditor-block {
    text-align: left;
  }

  .DraftEditor-alignLeft .public-DraftEditorPlaceholder-root {
    left: 0;
    text-align: left;
  }

  .DraftEditor-alignCenter .public-DraftEditor-block {
    text-align: center;
  }

  .DraftEditor-alignCenter .public-DraftEditorPlaceholder-root {
    margin: 0 auto;
    text-align: center;
    width: 100%;
  }

  .DraftEditor-alignRight .public-DraftEditor-block {
    text-align: right;
  }

  .DraftEditor-alignRight .public-DraftEditorPlaceholder-root {
    right: 0;
    text-align: right;
  }
  /**
 * @providesModule DraftEditorPlaceholder
 */

  .public-DraftEditorPlaceholder-root {
    color: #9197a3;
    position: absolute;
    z-index: 0;
  }

  .public-DraftEditorPlaceholder-hasFocus {
    color: #bdc1c9;
  }

  .DraftEditorPlaceholder-hidden {
    display: none;
  }
  /**
 * @providesModule DraftStyleDefault
 */

  .public-DraftStyleDefault-block {
    position: relative;
    white-space: pre-wrap;
  }

  /* @noflip */

  .public-DraftStyleDefault-ltr {
    direction: ltr;
    text-align: left;
  }

  /* @noflip */

  .public-DraftStyleDefault-rtl {
    direction: rtl;
    text-align: right;
  }

  /**
  * These rules provide appropriate text direction for counter pseudo-elements.
  */

  /* @noflip */

  .public-DraftStyleDefault-listLTR {
    direction: ltr;
  }

  /* @noflip */

  .public-DraftStyleDefault-listRTL {
    direction: rtl;
  }

  /**
  * Default spacing for list container elements. Override with CSS as needed.
  */

  .public-DraftStyleDefault-ul,
  .public-DraftStyleDefault-ol {
    margin: 16px 0;
    padding: 0;
  }

  /**
  * Default counters and styles are provided for five levels of nesting.
  * If you require nesting beyond that level, you should use your own CSS
  * classes to do so. If you care about handling RTL languages, the rules you
  * create should look a lot like these.
  */

  /* @noflip */

  .public-DraftStyleDefault-depth0.public-DraftStyleDefault-listLTR {
    margin-left: 1.5em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth0.public-DraftStyleDefault-listRTL {
    margin-right: 1.5em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth1.public-DraftStyleDefault-listLTR {
    margin-left: 3em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth1.public-DraftStyleDefault-listRTL {
    margin-right: 3em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth2.public-DraftStyleDefault-listLTR {
    margin-left: 4.5em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth2.public-DraftStyleDefault-listRTL {
    margin-right: 4.5em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth3.public-DraftStyleDefault-listLTR {
    margin-left: 6em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth3.public-DraftStyleDefault-listRTL {
    margin-right: 6em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth4.public-DraftStyleDefault-listLTR {
    margin-left: 7.5em;
  }

  /* @noflip */

  .public-DraftStyleDefault-depth4.public-DraftStyleDefault-listRTL {
    margin-right: 7.5em;
  }

  /**
 * Only use square list-style after the first two levels.
 */

  .public-DraftStyleDefault-unorderedListItem {
    list-style-type: square;
    position: relative;
  }

  .public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth0 {
    list-style-type: disc;
  }

  .public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth1 {
    list-style-type: circle;
  }

  /**
  * Ordered list item counters are managed with CSS, since all list nesting is
  * purely visual.
  */

  .public-DraftStyleDefault-orderedListItem {
    list-style-type: none;
    position: relative;
  }

  /* @noflip */

  .public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-listLTR:before {
    left: -36px;
    position: absolute;
    text-align: right;
    width: 30px;
  }

  /* @noflip */

  .public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-listRTL:before {
    position: absolute;
    right: -36px;
    text-align: left;
    width: 30px;
  }

  /**
  * Counters are reset in JavaScript. If you need different counter styles,
  * override these rules. If you need more nesting, create your own rules to
  * do so.
  */

  .public-DraftStyleDefault-orderedListItem:before {
    content: counter(ol0) '. ';
    counter-increment: ol0;
  }

  .public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-depth1:before {
    content: counter(ol1) '. ';
    counter-increment: ol1;
  }

  .public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-depth2:before {
    content: counter(ol2) '. ';
    counter-increment: ol2;
  }

  .public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-depth3:before {
    content: counter(ol3) '. ';
    counter-increment: ol3;
  }

  .public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-depth4:before {
    content: counter(ol4) '. ';
    counter-increment: ol4;
  }

  .public-DraftStyleDefault-depth0.public-DraftStyleDefault-reset {
    counter-reset: ol0;
  }

  .public-DraftStyleDefault-depth1.public-DraftStyleDefault-reset {
    counter-reset: ol1;
  }

  .public-DraftStyleDefault-depth2.public-DraftStyleDefault-reset {
    counter-reset: ol2;
  }

  .public-DraftStyleDefault-depth3.public-DraftStyleDefault-reset {
    counter-reset: ol3;
  }

  .public-DraftStyleDefault-depth4.public-DraftStyleDefault-reset {
    counter-reset: ol4;
  }
`;

export const FlexContainer = styled.div`
  display: flex;
`;

export const FlexRow = styled(FlexContainer)<{
  justifyContent?: string;
  makeColumn?: boolean;
}>`
  flex-direction: row;

  ${props =>
    props.justifyContent &&
    css`
      justify-content: ${props.justifyContent};
    `}

  ${props =>
    props.makeColumn &&
    css`
      flex-direction: column;
    `}
`;

export const FlexColumn = styled(FlexContainer)`
  flex-direction: column;
`;

export const CommentInput = styled.input`
  display: flex;
  border-radius: ${borderRadius('small')};
  padding: 0.5rem;
  border: 1px solid ${color('lightGrey')};
  width: 100%;
  font-size: 16px;
  outline: none;
  color: ${color('secondaryBrand')};
  margin: 0.5rem 0;
  resize: vertical;

  ::placeholder {
    color: ${color('grey')};
  }
`;

export const SmallButton = styled(PlainButton).attrs(() => ({
  color: 'grey',
  size: 'caption_new',
}))<{ disabled?: boolean }>`
  margin-right: 1rem;
  outline: none;

  &:hover {
    color: ${color('secondaryBrand')};
  }

  ${props =>
    props.disabled &&
    css`
      cursor: default;
      font-size: 0.75rem;
      color: ${color('lightGrey')};
      &:hover {
        color: ${color('lightGrey')};
      }
    `}
`;

const highlight = keyframes`
  from {
    background-color: ${color('lightGrey')({ theme: Theme })};
  }

  to {
    background-color: inherit;
  }
`;

export const CommentWrapper = styled.div<{
  isEditing?: boolean;
  isApproved?: boolean;
  isReply?: boolean;
  canApprove?: boolean;
}>`
  display: flex;
  transition: all 700ms ease-in-out;
  margin-top: 1rem;

  &.highlight {
    animation: 10s ease-in-out ${highlight};
  }

  ${props =>
    props.isReply &&
    css`
      padding: 0 1rem 0 0;
    `}

  ${props =>
    props.isEditing &&
    css`
      padding: 1rem;
    `}

    ${props =>
    !props.isApproved &&
    !props.isReply &&
    css`
      background-color: ${color('sidebarGrey')};
      padding: 1rem;
      border-radius: 5px;
    `}

    ${props =>
    props.canApprove &&
    !props.isReply &&
    css`
      background-color: ${color('sidebarGrey')};
      padding-top: 0.5rem;
    `}
`;

export const BackDrop = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: rgba(66, 77, 93, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${zIndex('modal')};
  top: 0;
  left: 0;
`;

export const PresenterChip = styled.span`
  ${borderRadius('small')};
  background-color: ${color('grey')};
  padding: 1px 4px;
  font-size: 12px;
  color: ${color('white')};
`;

export const MentionTag = styled.span`
  cursor: pointer;
  text-transform: capitalize;

  a {
    all: unset;
  }
`;

export const MentionsEditor = styled.div<{
  noBorder?: boolean;
  readOnly?: boolean;
  mentionTagHasBg?: boolean;
}>`
  border-radius: 3px;
  padding: 0.5rem;
  border: 1px solid ${color('lightGrey')};
  width: 100%;
  font-size: 16px;
  outline: none;
  color: ${color('secondaryBrand')};
  margin: 0.5rem 0;
  line-height: 1.5rem;
  cursor: text;

  &:focus-within {
    border: 1px solid ${color('secondaryBrand')} !important;
    box-shadow: 0 0 0px 2px ${color('lightGrey')};
  }

  ${props =>
    props.noBorder &&
    css`
      border: unset;
    `}

  ${props =>
    props.readOnly &&
    css`
      border: unset;
      padding: unset;
      margin: 0;
    `}

  .DraftEditor-root {
    position: relative;
    z-index: 0;
  }

  ${MentionTag} {
    ${props =>
      props.mentionTagHasBg
        ? css`
            background-color: ${color('sidebarGrey')};
            padding: 2px;
          `
        : css`
            font-weight: 600;
          `}
  }
`;

export const DraftWrapper = styled(DraftStyleWrapper).attrs(() => ({
  hasBanner: false,
}))`
  word-break: break-word;
`;

// copied styles from draft-js-mention-plugin/lib/plugin.css
export const SuggestionsContainer = styled.div`
  .draftJsMentionPlugin__mention__29BEd,
  .draftJsMentionPlugin__mention__29BEd:visited {
    color: red;
    cursor: pointer;
    display: inline-block;
    background: #e6f3ff;
    padding-left: 2px;
    padding-right: 2px;
    border-radius: 2px;
    text-decoration: none;
  }

  .draftJsMentionPlugin__mention__29BEd:hover,
  .draftJsMentionPlugin__mention__29BEd:focus {
    color: #677584;
    background: #edf5fd;
    outline: 0; /* reset for :focus */
  }

  .draftJsMentionPlugin__mention__29BEd:active {
    color: #222;
    background: #455261;
  }
  .draftJsMentionPlugin__mentionSuggestionsEntry__3mSwm {
    padding: 7px 10px 3px 10px;
    transition: background-color 0.4s cubic-bezier(0.27, 1.27, 0.48, 0.56);
  }

  .draftJsMentionPlugin__mentionSuggestionsEntry__3mSwm:active {
    background-color: ${color('sidebarGrey')};
  }

  .draftJsMentionPlugin__mentionSuggestionsEntryFocused__3LcTd {
    background-color: ${color('sidebarGrey')};
  }

  .draftJsMentionPlugin__mentionSuggestionsEntryText__3Jobq {
    display: inline-block;
    margin-left: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 368px;
    font-size: 0.9em;
    margin-bottom: 0.2em;
  }

  .draftJsMentionPlugin__mentionSuggestionsEntryAvatar__1xgA9 {
    display: inline-block;
    width: 24px;
    height: 24px;
    border-radius: 12px;
    object-fit: cover;
  }
  .draftJsMentionPlugin__mentionSuggestions__2DWjA {
    border: 1px solid #eee;
    margin-top: 0.4em;
    position: absolute;
    min-width: 220px;
    max-width: 440px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0px 4px 30px 0px rgba(220, 220, 220, 1);
    cursor: pointer;
    padding: 4px 1px;
    z-index: 2;
    display: -webkit-box;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    flex-direction: column;
    box-sizing: border-box;
    -webkit-transform: scale(0);
    transform: scale(0);
  }
`;
